import DataService from './DataService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * API DATA SERVICE - REAL BACKEND INTEGRATION
 * 
 * Purpose: Connect frontend to actual FastAPI backend
 * - All CRUD operations through real APIs
 * - Proper error handling
 * - File upload support
 */

class ApiDataService extends DataService {
  constructor() {
    super();
    // Add request interceptor for debugging
    axios.interceptors.request.use(request => {
      console.log('Starting Request:', request.url);
      return request;
    });

    axios.interceptors.response.use(
      response => response,
      error => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  async getPosts(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category);
      }
      if (filters.tag && filters.tag !== 'all') {
        params.append('tag', filters.tag);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }

      const response = await axios.get(`${API}/posts?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  }

  async getPost(slug) {
    try {
      const response = await axios.get(`${API}/posts/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  }

  async createPost(postData) {
    try {
      const response = await axios.post(`${API}/posts`, postData);
      return response.data;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  async updatePost(postId, postData) {
    try {
      const response = await axios.put(`${API}/posts/${postId}`, postData);
      return response.data;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  }

  async deletePost(postId) {
    try {
      const response = await axios.delete(`${API}/posts/${postId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

  async getCategories() {
    try {
      const response = await axios.get(`${API}/categories`);
      return response.data.categories || response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async createCategory(categoryData) {
    try {
      const response = await axios.post(`${API}/categories`, categoryData);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(categoryId, categoryData) {
    try {
      const response = await axios.put(`${API}/categories/${categoryId}`, categoryData);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId) {
    try {
      const response = await axios.delete(`${API}/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  async getPostsByCategory(categorySlug) {
    try {
      const response = await axios.get(`${API}/posts?category=${categorySlug}`);
      return response.data.posts || response.data;
    } catch (error) {
      console.error('Error fetching posts by category:', error);
      throw error;
    }
  }

  async getFeaturedPosts() {
    try {
      const response = await axios.get(`${API}/posts/featured`);
      return response.data;
    } catch (error) {
      console.error('Error fetching featured posts:', error);
      throw error;
    }
  }

  async getProfile() {
    try {
      const response = await axios.get(`${API}/profile`);
      return response.data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await axios.put(`${API}/profile`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  async getCareerExperience() {
    try {
      const response = await axios.get(`${API}/career`);
      return response.data.experiences || response.data;
    } catch (error) {
      console.error('Error fetching career experience:', error);
      throw error;
    }
  }

  async createCareerExperience(experienceData) {
    try {
      const response = await axios.post(`${API}/career`, experienceData);
      return response.data;
    } catch (error) {
      console.error('Error creating career experience:', error);
      throw error;
    }
  }

  async updateCareerExperience(experienceId, experienceData) {
    try {
      const response = await axios.put(`${API}/career/${experienceId}`, experienceData);
      return response.data;
    } catch (error) {
      console.error('Error updating career experience:', error);
      throw error;
    }
  }

  async deleteCareerExperience(experienceId) {
    try {
      const response = await axios.delete(`${API}/career/${experienceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting career experience:', error);
      throw error;
    }
  }

  async getEducation() {
    try {
      const response = await axios.get(`${API}/education`);
      // Backend returns array directly, not wrapped in object
      const educationData = Array.isArray(response.data) ? response.data : (response.data.education || []);
      // Normalize education data to match frontend expectations
      return educationData.map(edu => ({
        ...edu,
        // Map 'field' to 'grade' if grade is missing (for backward compatibility)
        grade: edu.grade || edu.field || '',
        // Ensure achievements is always an array
        achievements: Array.isArray(edu.achievements) ? edu.achievements : [],
        // Ensure featured field exists (default to true if missing)
        featured: edu.featured !== undefined ? edu.featured : true
      }));
    } catch (error) {
      console.error('Error fetching education:', error);
      throw error;
    }
  }

  async createEducation(educationData) {
    try {
      const response = await axios.post(`${API}/education`, educationData);
      return response.data;
    } catch (error) {
      console.error('Error creating education:', error);
      throw error;
    }
  }

  async updateEducation(educationId, educationData) {
    try {
      const response = await axios.put(`${API}/education/${educationId}`, educationData);
      return response.data;
    } catch (error) {
      console.error('Error updating education:', error);
      throw error;
    }
  }

  async deleteEducation(educationId) {
    try {
      const response = await axios.delete(`${API}/education/${educationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting education:', error);
      throw error;
    }
  }

  async getAwards() {
    try {
      const response = await axios.get(`${API}/awards`);
      return response.data.awards || response.data;
    } catch (error) {
      console.error('Error fetching awards:', error);
      throw error;
    }
  }

  async getPublications() {
    try {
      const response = await axios.get(`${API}/publications`);
      return response.data.publications || response.data;
    } catch (error) {
      console.error('Error fetching publications:', error);
      throw error;
    }
  }

  async createAward(awardData) {
    try {
      const response = await axios.post(`${API}/awards`, awardData);
      return response.data;
    } catch (error) {
      console.error('Error creating award:', error);
      throw error;
    }
  }

  async updateAward(awardId, awardData) {
    try {
      const response = await axios.put(`${API}/awards/${awardId}`, awardData);
      return response.data;
    } catch (error) {
      console.error('Error updating award:', error);
      throw error;
    }
  }

  async deleteAward(awardId) {
    try {
      const response = await axios.delete(`${API}/awards/${awardId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting award:', error);
      throw error;
    }
  }

  async getProjects() {
    try {
      const response = await axios.get(`${API}/initiatives`);
      return response.data.projects || response.data;
    } catch (error) {
      console.error('Error fetching initiatives:', error);
      throw error;
    }
  }

  async createProject(projectData) {
    try {
      const response = await axios.post(`${API}/initiatives`, projectData);
      return response.data;
    } catch (error) {
      console.error('Error creating initiative:', error);
      throw error;
    }
  }

  async updateProject(projectId, projectData) {
    try {
      const response = await axios.put(`${API}/initiatives/${projectId}`, projectData);
      return response.data;
    } catch (error) {
      console.error('Error updating initiative:', error);
      throw error;
    }
  }

  async deleteProject(projectId) {
    try {
      const response = await axios.delete(`${API}/initiatives/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting initiative:', error);
      throw error;
    }
  }

  async uploadFile(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async getAnalytics() {
    try {
      const response = await axios.get(`${API}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }
}

export default ApiDataService;